// src/components/TaskListItem.jsx
import { Card, CardContent, Typography, Button, Box, Chip, Stack } from '@mui/material';
import { Place, Schedule, CameraAlt, CheckCircle } from '@mui/icons-material';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyFormatter';
import { useNavigate } from 'react-router-dom';

export default function TaskListItem({ task }) { // Remove isSelected and onSelect props
  // Safe data access with fallbacks
  const taskTitle = task?.title || 'Untitled Task';
  const taskDescription = task?.description || 'No description available';
  const taskCategory = task?.category || 'General';
  const taskReward = task?.reward || 0; // Default to 0 if undefined
  const taskDistance = task?.distance || 'N/A';
  const taskDeadline = task?.deadline || 'No deadline';
  const taskPostedTime = task?.postedTime || 'Recently';
  const currencySymbol = getCurrencySymbol(task);
  const formattedReward = formatCurrency(taskReward, currencySymbol);
  const navigate = useNavigate();

  // Handler for when the entire card is clicked
  const handleCardClick = () => {
    navigate(`/task/${task.id}`);
  };

  // Handler for START button click
  const handleStartButtonClick = (e) => {
    e.stopPropagation(); // Prevent the card click event from triggering
    navigate(`/task/${task.id}`); // Navigate to task detail page
  };

  // Function to get icon based on category
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Photography': return <CameraAlt />;
      case 'Verification': return <CheckCircle />;
      default: return <Place />;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        '&:hover': {
          backgroundColor: 'action.hover'
        },
        transition: 'all 0.2s ease'
      }}
      onClick={handleCardClick} // Whole card click navigates to task detail
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Left side: Task Info */}
          <Box sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                icon={getCategoryIcon(taskCategory)}
                label={taskCategory}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Typography variant="body2" color="text.secondary">
                {taskPostedTime}
              </Typography>
            </Stack>
            
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontSize: '1.1rem' }}>
              {taskTitle}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 1.5 }}>
              {taskDescription.substring(0, 80)}...
            </Typography>
            
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Place fontSize="small" color="action" />
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  {taskDistance} km away
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule fontSize="small" color="action" />
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                  Due: {taskDeadline}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Right side: Reward and CTA */}
          <Box sx={{ textAlign: 'right', ml: 2, minWidth: '100px' }}>
            <Typography variant="h6" color="success.main" gutterBottom sx={{ fontWeight: 700 }}>
              {formattedReward}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleStartButtonClick}
            >
              Start
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}